/**
 * QRScanModal + QRScanButton — camera-based QR/barcode scanner.
 *
 * Extracted from src/pages/stores/BaleManagementPage.tsx for reuse across
 * the bale-management workflow (Phase BM2 chunk D-4).
 *
 * Uses the native BarcodeDetector API (Chrome 83+ / Edge 83+ / Safari 17+).
 * No npm package required.
 *
 * Supported codes: QR · Code 128 · Code 39 · Code 93 · EAN-13 · EAN-8 ·
 *                  Data Matrix · ITF
 *
 * Falls back gracefully when the browser is too old or no camera is found:
 * shows a clear message and tells the operator to use a USB barcode scanner
 * instead (works in any browser, no permission prompts).
 *
 * Usage:
 *   <QRScanButton onScan={(text) => addBaleByCode(text)} />
 *   ← renders a button with camera icon. Tap → modal opens. Scan → callback fires.
 *
 *   Or use the modal directly:
 *   <QRScanModal open={open} onScan={...} onClose={...} />
 */
import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal, Select, Space, Spin, Typography } from 'antd'
import {
  CameraOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import jsQR from 'jsqr'

const { Text } = Typography

// ── Helpers (success feedback) ──────────────────────────────────────────────

function playBeep(freq = 880, dur = 0.12) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Ctx = window.AudioContext || (window as any).webkitAudioContext
    const ctx = new Ctx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.frequency.value = freq
    osc.type = 'sine'
    gain.gain.setValueAtTime(0.4, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur)
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + dur)
  } catch { /* audio failure is non-critical */ }
}

function vibrate(pattern: number[] = [80]) {
  try { if ('vibrate' in navigator) navigator.vibrate(pattern) } catch { /* ignore */ }
}

// ── Constants ────────────────────────────────────────────────────────────────

type ScanPhase = 'loading' | 'scanning' | 'success' | 'error'

const BARCODE_FORMATS = [
  'qr_code', 'code_128', 'code_39', 'code_93',
  'ean_13', 'ean_8', 'data_matrix', 'itf',
]

// ═══════════════════════════════════════════════════════════════════════════
// QRScanModal — the actual camera viewport
// ═══════════════════════════════════════════════════════════════════════════

type ModalProps = {
  open: boolean
  onScan: (value: string) => void
  onClose: () => void
  /** Title shown in modal header. Default: "Scan QR / Barcode". */
  title?: string
}

export function QRScanModal({ open, onScan, onClose, title }: ModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- BarcodeDetector ambient type not always present
  const detectorRef = useRef<any>(null)
  // jsQR fallback flag — true when we're using the WASM-free JS decoder
  // because the browser doesn't expose BarcodeDetector (Firefox, older
  // Safari, some Chromium derivatives).
  const useFallbackRef = useRef(false)
  const doneRef = useRef(false)

  const [phase, setPhase] = useState<ScanPhase>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [scannedVal, setScannedVal] = useState('')
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [currentCam, setCurrentCam] = useState<string | undefined>()

  // We no longer hard-block when BarcodeDetector is missing — the jsQR
  // fallback handles that. We DO still need the camera (getUserMedia)
  // available, but that's been in every browser since Chrome 53.
  const cameraSupported =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'

  useEffect(() => {
    if (!open) return
    doneRef.current = false

    if (!cameraSupported) {
      setErrorMsg(
        'This browser does not expose camera access (mediaDevices.getUserMedia). ' +
        'Open the page in a modern browser (Chrome / Edge / Safari / Firefox) ' +
        'or plug in a USB barcode scanner — that works in any browser.'
      )
      setPhase('error')
      return
    }

    navigator.mediaDevices.enumerateDevices()
      .then(devs => {
        const vids = devs.filter(d => d.kind === 'videoinput')
        setCameras(vids)
        const back = vids.find(d => /back|rear|environment/i.test(d.label))
        const id = back?.deviceId ?? vids[0]?.deviceId
        setCurrentCam(id)
        return openCamera(id)
      })
      .catch(() => openCamera(undefined))

    return () => closeCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function openCamera(deviceId?: string) {
    closeCamera()
    doneRef.current = false
    setPhase('loading')
    setScannedVal('')

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      streamRef.current = stream

      const vid = videoRef.current!
      vid.srcObject = stream
      await vid.play()

      // Try native BarcodeDetector first — fastest and supports the
      // widest set of formats (Code 128, EAN, etc., not just QR).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const BD = (window as any).BarcodeDetector
      if (BD) {
        try {
          let fmts = BARCODE_FORMATS
          try {
            const sup: string[] = await BD.getSupportedFormats()
            fmts = BARCODE_FORMATS.filter(f => sup.includes(f))
          } catch { /* getSupportedFormats may not exist on older impls */ }
          detectorRef.current = new BD({ formats: fmts.length ? fmts : ['qr_code'] })
          useFallbackRef.current = false
        } catch {
          // BarcodeDetector exists but threw — drop to jsQR fallback.
          detectorRef.current = null
          useFallbackRef.current = true
        }
      } else {
        // No BarcodeDetector at all (Firefox, older Safari, Chromium
        // derivatives without the API). jsQR is bundled — works on
        // every browser that supports getUserMedia + canvas.
        useFallbackRef.current = true
      }

      setPhase('scanning')
      scanLoop()
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string }
      const map: Record<string, string> = {
        NotAllowedError:
          'Camera permission denied.\n\nClick the 🔒 icon in the browser address bar and allow camera access, then try again.',
        NotFoundError:
          'No camera found on this device. Connect a USB barcode scanner — it works without camera.',
      }
      setErrorMsg(map[e.name ?? ''] ?? `Camera error: ${e.message ?? 'unknown'}`)
      setPhase('error')
    }
  }

  async function scanLoop() {
    if (doneRef.current || !videoRef.current) return
    const vid = videoRef.current
    if (!vid.paused && vid.readyState >= 2) {
      try {
        let val: string | null = null

        if (detectorRef.current && !useFallbackRef.current) {
          // Native BarcodeDetector path
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const results: any[] = await detectorRef.current.detect(vid)
          if (results.length && results[0].rawValue) val = results[0].rawValue
        } else {
          // jsQR fallback — copy the current video frame to a hidden
          // canvas and feed the pixel data to jsQR. QR-codes only;
          // good enough for the bale stickers.
          const canvas = canvasRef.current
          if (canvas) {
            const w = vid.videoWidth || 640
            const h = vid.videoHeight || 480
            if (w && h) {
              canvas.width = w
              canvas.height = h
              const ctx = canvas.getContext('2d', { willReadFrequently: true })
              if (ctx) {
                ctx.drawImage(vid, 0, 0, w, h)
                const img = ctx.getImageData(0, 0, w, h)
                const code = jsQR(img.data, w, h, { inversionAttempts: 'attemptBoth' })
                if (code && code.data) val = code.data
              }
            }
          }
        }

        if (val) {
          doneRef.current = true
          setScannedVal(val)
          setPhase('success')
          playBeep(1046, 0.1)        // high C — success tone
          vibrate([60, 30, 60])
          setTimeout(() => { closeCamera(); onScan(val!) }, 700)
          return
        }
      } catch { /* detect can throw if frame not ready */ }
    }
    rafRef.current = requestAnimationFrame(scanLoop)
  }

  function closeCamera() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }

  function switchCamera(id: string) {
    setCurrentCam(id)
    openCamera(id)
  }

  return (
    <Modal
      open={open}
      onCancel={() => { closeCamera(); onClose() }}
      footer={null}
      title={<Space><CameraOutlined />{title ?? 'Scan QR / Barcode'}</Space>}
      width={480}
      centered
      destroyOnClose
    >
      <div style={{
        position: 'relative', background: '#111', borderRadius: 10,
        overflow: 'hidden', minHeight: 280,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: '100%', maxHeight: 340, objectFit: 'cover', display: 'block' }}
        />
        {/* Hidden canvas — used by the jsQR fallback to grab video
            frames as ImageData. The native BarcodeDetector path doesn't
            need it, but allocating it unconditionally is cheap (a single
            DOM node) and avoids race conditions during browser swaps. */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {phase === 'loading' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <Spin size="large" />
            <Text style={{ color: '#fff', marginTop: 12, fontSize: 13 }}>Starting camera…</Text>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ padding: '20px 16px' }}>
            <Alert
              type="warning"
              showIcon
              message="Camera unavailable"
              description={<span style={{ whiteSpace: 'pre-line' }}>{errorMsg}</span>}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                💡 USB barcode scanners require no camera — just plug in and scan directly
              </Text>
            </div>
          </div>
        )}

        {phase === 'scanning' && (
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 80px rgba(0,0,0,0.55)' }} />
            <div style={{ position: 'relative', width: 230, height: 230 }}>
              {[
                { top: -2, left: -2, borderTop: '3px solid #52c41a', borderLeft: '3px solid #52c41a', borderTopLeftRadius: 4 },
                { top: -2, right: -2, borderTop: '3px solid #52c41a', borderRight: '3px solid #52c41a', borderTopRightRadius: 4 },
                { bottom: -2, left: -2, borderBottom: '3px solid #52c41a', borderLeft: '3px solid #52c41a', borderBottomLeftRadius: 4 },
                { bottom: -2, right: -2, borderBottom: '3px solid #52c41a', borderRight: '3px solid #52c41a', borderBottomRightRadius: 4 },
              ].map((s, i) => (
                <div key={i} style={{ position: 'absolute', width: 30, height: 30, ...s }} />
              ))}
              <div style={{
                position: 'absolute', left: 16, right: 16, top: '50%', height: 1,
                background: 'rgba(82,196,26,0.6)',
              }} />
            </div>
          </div>
        )}

        {phase === 'success' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(82,196,26,0.88)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircleOutlined style={{ fontSize: 60, color: '#fff', marginBottom: 10 }} />
            <Text style={{ color: '#fff', fontSize: 17, fontWeight: 700 }}>Scanned!</Text>
            <Text style={{ color: '#ffffffcc', fontFamily: 'monospace', fontSize: 12, marginTop: 6 }}>
              {scannedVal}
            </Text>
          </div>
        )}
      </div>

      {cameras.length > 1 && phase !== 'error' && (
        <Select
          style={{ width: '100%', marginTop: 12 }}
          size="small"
          value={currentCam}
          onChange={switchCamera}
          options={cameras.map((c, i) => ({
            value: c.deviceId,
            label: c.label || `Camera ${i + 1}`,
          }))}
          placeholder="Select camera"
        />
      )}

      <div style={{ textAlign: 'center', marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {useFallbackRef.current
            ? 'Supports: QR codes (browser-fallback decoder)'
            : 'Supports: QR · Code 128 · Code 39 · EAN-13 · Data Matrix · ITF'}
        </Text>
      </div>
    </Modal>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// QRScanButton — the trigger button that bundles the modal state
// ═══════════════════════════════════════════════════════════════════════════

type ButtonProps = {
  onScan: (value: string) => void
  /** AntD button size. Default: 'middle'. Use 'large' for tablet/operator screens. */
  size?: 'small' | 'middle' | 'large'
  /** Button label. Default: "Scan". */
  label?: string
  /** Title shown in modal header. */
  modalTitle?: string
  /** Custom button style (e.g. tablet-sized for QuickLaydownPage). */
  style?: React.CSSProperties
  /** Disable the trigger button. */
  disabled?: boolean
  /** Use 'primary' button type. */
  primary?: boolean
}

export function QRScanButton({
  onScan, size, label, modalTitle, style, disabled, primary,
}: ButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        type={primary ? 'primary' : 'default'}
        icon={<CameraOutlined />}
        size={size ?? 'middle'}
        style={style}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {label ?? 'Scan'}
      </Button>
      <QRScanModal
        open={open}
        title={modalTitle}
        onScan={(v) => { setOpen(false); onScan(v) }}
        onClose={() => setOpen(false)}
      />
    </>
  )
}
